import { MapPin } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface AddressSuggestion {
  description: string;
  placeId: string;
}

export interface AddressDetails {
  street?: string;
  houseNumber?: string;
  zip?: string;
  city?: string;
  latitude?: number;
  longitude?: number;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  /** Called with structured address components after a suggestion is selected. */
  onAddressDetails?: (details: AddressDetails) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  id?: string;
  /** Compact mode for dense address lists (e.g. CalendarImport) */
  compact?: boolean;
}

export default function AddressAutocomplete({
  value,
  onChange,
  onAddressDetails,
  placeholder = 'Indtast adresse',
  label,
  disabled = false,
  required = false,
  className = '',
  id,
  compact = false,
}: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Session token groups autocomplete + selection into one billing event
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const refreshSessionToken = () => {
    if (window.google?.maps?.places) {
      sessionToken.current = new google.maps.places.AutocompleteSessionToken();
    }
  };

  useEffect(() => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
    if (!apiKey) return;

    // Already loaded
    if (window.google?.maps?.places) {
      refreshSessionToken();
      return;
    }

    // Already loading (another instance beat us to it)
    if (document.querySelector('script[data-maps-loader]')) {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          refreshSessionToken();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.dataset.mapsLoader = 'true';
    script.onload = () => refreshSessionToken();
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddress = async (query: string) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    if (!window.google?.maps?.places?.AutocompleteSuggestion) {
      // Google Maps not loaded yet — silently skip
      return;
    }

    setIsLoading(true);

    try {
      const { suggestions: results } =
        await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          ...(sessionToken.current ? { sessionToken: sessionToken.current } : {}),
          includedRegionCodes: ['dk'],
        });

      setSuggestions(
        results
          .filter((s) => s.placePrediction !== null)
          .map((s) => ({
            description: s.placePrediction!.text.text,
            placeId: s.placePrediction!.placeId,
          }))
      );
      setShowSuggestions(true);
    } catch (error) {
      console.error('Error searching address:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (newValue: string) => {
    onChange(newValue);
    setSelectedIndex(-1);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      searchAddress(newValue);
    }, 300);
  };

  const handleSelectSuggestion = async (suggestion: AddressSuggestion) => {
    onChange(suggestion.description);
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedIndex(-1);
    // Start a new session token for the next autocomplete interaction
    refreshSessionToken();

    if (onAddressDetails && window.google?.maps?.places?.Place) {
      try {
        const place = new google.maps.places.Place({ id: suggestion.placeId });
        await place.fetchFields({ fields: ['addressComponents', 'location'] });

        const components = place.addressComponents ?? [];
        const get = (...types: string[]) =>
          components.find((c) => types.some((t) => (c.types as string[]).includes(t)))?.longText ?? undefined;

        const details: AddressDetails = {};
        const street = get('route');
        const houseNumber = get('street_number');
        const zip = get('postal_code');
        const city = get('locality', 'postal_town', 'sublocality_level_1');
        const lat = place.location?.lat();
        const lng = place.location?.lng();
        if (street !== undefined) details.street = street;
        if (houseNumber !== undefined) details.houseNumber = houseNumber;
        if (zip !== undefined) details.zip = zip;
        if (city !== undefined) details.city = city;
        if (lat !== undefined) details.latitude = lat;
        if (lng !== undefined) details.longitude = lng;
        onAddressDetails(details);
      } catch {
        // Structured data unavailable — caller only gets the address string
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        handleSelectSuggestion(suggestions[selectedIndex]!);
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  if (compact) {
    return (
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <MapPin className={`w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none ${value ? 'text-gray-400' : 'text-amber-500'}`} />
          <input
            id={id}
            type="text"
            value={value}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => value.length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            autoComplete="off"
            className={`w-full pl-6 pr-6 py-1 text-xs border rounded focus:ring-1 focus:ring-blue-500 focus:outline-none ${
              !value ? 'border-amber-300 bg-amber-50 placeholder-amber-400' : 'border-gray-200'
            } ${className}`}
          />
          {isLoading && (
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none">
              <div className="animate-spin rounded-full h-3 w-3 border border-blue-600 border-t-transparent" />
            </div>
          )}
        </div>

        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute z-50 w-full min-w-[220px] mt-0.5 bg-white border border-gray-200 rounded-lg shadow-xl max-h-48 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <button
                key={suggestion.placeId}
                type="button"
                onClick={() => handleSelectSuggestion(suggestion)}
                className={`w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                  index === selectedIndex ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-start gap-1.5">
                  <MapPin className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-gray-800 leading-tight">{suggestion.description}</span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          id={id}
          type="text"
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => value.length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
          disabled={disabled}
          required={required}
          className={`w-full px-4 py-3.5 pr-10 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed ${className}`}
          placeholder={placeholder}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          {isLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 border-t-transparent" />
          ) : (
            <MapPin className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.placeId}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`w-full text-left px-4 py-3 hover:bg-blue-50 transition-colors border-b border-gray-100 last:border-b-0 ${
                index === selectedIndex ? 'bg-blue-50' : ''
              }`}
            >
              <div className="flex items-start space-x-2">
                <MapPin className="w-4 h-4 text-blue-600 mt-1 flex-shrink-0" />
                <span className="text-sm text-gray-900">{suggestion.description}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showSuggestions && !isLoading && suggestions.length === 0 && value.length >= 3 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
          <p className="text-sm text-gray-500 text-center">Ingen adresser fundet</p>
        </div>
      )}
    </div>
  );
}
